import { FunctionComponent, useEffect, useState } from 'react'
import { SuggestParentProfile, SuggestSettingsResponse } from '@/types/Suggest'
import ButtonTextIcon from '@/components/_custom/Buttons/ButtonTextIcon/ButtonTextIcon'
import { useLazyQuery } from '@apollo/client'
import { GET_SUGGEST_PARENTS } from '@/modules/API/suggest/suggestHandler'
import Loading from '@/components/Loading/Loading'
import ErrorMessage from '@/components/_custom/ErrorMessage/ErrorMessage'
import { useTranslation } from 'react-i18next'
import CitySelect from '@/components/_custom/CitySelect/citySelect'
import UserInput from '@/components/_custom/UserInput/userInput'
import styles from './CALDParentSettings.module.scss'
import CountrySelect from '@/components/_custom/CountrySelect/countrySelect'
import { GET_SUGGEST_SETTINGS } from '@/modules/API/daycare/daycareSuggestHandler'
import { useAuth } from '@/modules/context/AuthContext'

interface CALDParentSettingsProps {
    setAllSuggest: (allSuggest: SuggestParentProfile[]) => void
    isNanny: boolean
    setCALDRequest: (CALDRequest: boolean) => void
    ageMin: string
    setAgeMin: (value: string) => void
    ageMax: string
    setAgeMax: (value: string) => void
}

const CALDParentSettings: FunctionComponent<CALDParentSettingsProps> = ({
    setAllSuggest,
    isNanny,
    setCALDRequest,
    ageMin,
    setAgeMin,
    ageMax,
    setAgeMax
}: CALDParentSettingsProps) => {
    const { t } = useTranslation()

    const { user } = useAuth()

    const [daycareId, setDaycareId] = useState<number>(user?.currentDaycare?.id || 0)
    const [country, setCountry] = useState<string>(user?.currentDaycare?.country || '')
    const [city, setCity] = useState<string>(user?.currentDaycare?.city || '')
    const [range, setRange] = useState<string>('0')

    const [getSuggestSettings, { data: dataSuggestSettings }] = useLazyQuery<{
        getSuggestSettings: SuggestSettingsResponse
    }>(GET_SUGGEST_SETTINGS, {
        fetchPolicy: 'network-only',
        variables: {
            daycareId: daycareId
        }
    })

    useEffect(() => {
        if (dataSuggestSettings) {
            setRange(dataSuggestSettings.getSuggestSettings.parentDistance.split('_')[1])
        }
    }, [dataSuggestSettings])

    useEffect(() => {
        setDaycareId(user?.currentDaycare?.id || 0)
        setCountry(user?.currentDaycare?.country || '')
        setCity(user?.currentDaycare?.city || '')
        getSuggestSettings()
    }, [user?.currentDaycare])

    const [
        getSuggestParent,
        { loading: loadingGetSuggestParent, data: dataGetSuggestParent, error: errorGetSuggestParent }
    ] = useLazyQuery(GET_SUGGEST_PARENTS)

    useEffect(() => {
        if (dataGetSuggestParent) {
            setCALDRequest(true)
            setAllSuggest(dataGetSuggestParent.getSuggestParents)
        }
    }, [dataGetSuggestParent])

    useEffect(() => {
        if (errorGetSuggestParent) {
            setAllSuggest([])
            setCALDRequest(false)
        }
    }, [errorGetSuggestParent])

    function handleSearch(): void {
        getSuggestParent({
            variables: {
                daycareId: daycareId,
                isNanny: isNanny,
                country: country,
                city: city,
                range: Number(range),
                minMonth: Number(ageMin) * 12,
                maxMonth: (ageMax === '' ? 100 : Number(ageMax)) * 12
            }
        })
    }

    return (
        <>
            {loadingGetSuggestParent && <Loading overall />}
            <div className={styles.Container}>
                <div className={styles.UserInput}>
                    <CountrySelect country={country} setCountry={setCountry} />
                </div>
                <div className={styles.UserInput}>
                    <CitySelect city={city} setCity={setCity} country={country || 'FR'} />
                </div>

                <UserInput
                    type={'number'}
                    value={range}
                    setValue={setRange}
                    format={'num'}
                    minNumber={0}
                    maxNumber={1000}
                    verify={'number'}
                    className={styles.UserInput}
                    label={t('inRadiusOf')}
                    endAdornment={'km'}
                />
                <UserInput
                    type={'number'}
                    value={ageMin}
                    setValue={setAgeMin}
                    format={'num'}
                    minNumber={0}
                    maxNumber={ageMax === '' ? 100 : Number(ageMax)}
                    label={t('ageMinimum')}
                    verify={'number'}
                    className={styles.UserInput}
                    endAdornment={t('yearsOld')}
                />
                <UserInput
                    type={'number'}
                    value={ageMax}
                    setValue={setAgeMax}
                    format={'num'}
                    minNumber={Number(ageMin)}
                    maxNumber={100}
                    verify={'number'}
                    className={styles.UserInput}
                    label={t('ageMaximum')}
                    endAdornment={t('yearsOld')}
                />
                <ButtonTextIcon type={'confirm'} onClick={handleSearch} className={styles.Button} />
            </div>
            {errorGetSuggestParent && <ErrorMessage error={t('generalError')} />}
        </>
    )
}

export default CALDParentSettings
